import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import mapKeys from 'lodash/mapKeys';
import partition from 'lodash/partition';

import {Alignment, AlignmentOptions} from 'common/widgets/sankey-chart/models/types';
import {ResourceType} from 'common/report/constants';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {getSegmentType, isCohortSegment, Segment} from 'common/widgets/query-block/segment-menu/types';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {
  FiltersOperator,
  FlowsQueryEventEntry,
  QueryEventEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {PropertyItem} from 'common/widgets/query-block/properties-menu/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {fromEnum} from 'common/report/util/funnels/utils';
import {bookmarkGroupByToGroupBy, groupByToBookmarkGroupBy} from 'common/widgets/query-block/segment-menu/util';
import {bookmarkFilterByCohortToCohortFilters, getFilterByCohorts} from 'common/widgets/query-block/filter-menu/util';
import {SerializePropFilterContext} from 'common/report/util/arb';
import {Segmentation} from 'common/widgets/property-filter-menu/models/segfilter';

import {addFunnelBehaviorToFlowsBookmark} from '../funnels/utils';
import {BookmarkFunnelStep, CountType, FunnelBookmark} from '../funnels/types';
import {
  mapBookmarkExclusionStepToEventEntry,
  mapBookmarkStepToEventEntry,
  mapBookmarkToHoldPropConstant,
  mapEventEntryToBookmarkExclusionStep,
  mapEventEntryToBookmarkStep,
  mapHoldPropConstantToBookmark,
} from '../funnels/format';
import {
  AnchorPosition,
  CHART_TYPE_TO_QUERY_PARAM,
  ChartType,
  FlowAnchorPosition,
  FlowsBookmark,
  FunnelParamsForFlows,
} from './types';

export const FLOWS_REPORT_DEFAULTS = Object.freeze({
  alignment: [Alignment.RIGHT, Alignment.LEFT],
  alignBreakdowns: true,
  alwaysBreakdown: false,
  anchorPosition: FlowAnchorPosition.Step,
  binaryTag: null,
  cardinality: 3,
  chartType: ChartType.Sankey,
  cohortFilters: [],
  collapseRepeated: false,
  dashboard: null,
  dataGroupId: null,
  dateRange: new FullLengthDateRange().getAttributes(),
  description: null,
  events: [],
  eventFilters: [],
  exclusionSteps: [],
  funnelParams: null,
  graphSortPriority: `absolute`,
  groupBy: [],
  hiddenEvents: [],
  holdingPropertyConstant: [],
  id: null,
  listSortPriority: `end`,
  maxResults: 50,
  maxSteps: -1,
  name: null,
  otherFilters: [],
  projectId: -1,
  querySampling: false,
  relativeBreakdowns: false,
  reverse: false,
  segments: [],
  showCustomEvents: false,
  timePercentiles: [],
  title: null,
  uniqueFlows: false,
  userId: null,
  userName: null,
  userEmail: null,
  userFilters: [],
  version: `1`,
  visibleEvents: [],

  // permissions
  can_view: false,
  can_update_basic: false,
  can_share: false,
  is_superadmin: false,
  allow_staff_override: false,
});

/** Instantiates a PropertyFilter class from attributes */
export const constructPropertyFilters = (filterPrimitives) =>
  filterPrimitives.map((filter) => new PropertyFilter(filter));

export const BOOKMARK_PARAM_MAP = Object.freeze({
  collapse_repeated: `collapseRepeated`,
  hidden_events: `hiddenEvents`,
  visible_events: `visibleEvents`,
  graph_sort_priority: `graphSortPriority`,
  list_sort_priority: `listSortPriority`,
  segments: `segments`,
  show_custom_events: `showCustomEvents`,
});

const BOOKMARK_COPY_OVER_KEYS = Object.freeze([
  `alignment`,
  `anchorPosition`,
  `maxSteps`,
  `maxResults`,
  `cardinality`,
  `chartType`,
  `reverse`,
]);

const FLOWS_REPORT_DEFAULTS_KEYS = Object.keys(FLOWS_REPORT_DEFAULTS);

const BOOKMARK_PARAM_MAP_KEYS = Object.keys(BOOKMARK_PARAM_MAP);
const DEFAULTS = {...FLOWS_REPORT_DEFAULTS};
const URL_PARAMS = Object.keys(DEFAULTS);

export const CARDINALITY_FOR_DOWNLOAD = 10;
export const MAX_STEPS_FOR_DOWNLOAD = 15;
export const MAX_RESULTS_FOR_DOWNLOAD = 5;

export class FlowsReport {
  public static fromBookmark(bookmark): FlowsReport {
    const {
      params,
      name,
      id,
      userId,
      userName,
      userEmail,
      description,
      modifiedStr,

      /* eslint-disable @typescript-eslint/naming-convention */
      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,
      /* eslint-enable */
    } = bookmark;

    if (!isNaN(params.anchorPosition)) {
      params.anchorPosition = Object.values(FlowAnchorPosition)[params.anchorPosition];
    }

    const classParams = FlowsReport.bookmarkToClassParams(params);
    const convertedParams = mapKeys(pick(params, BOOKMARK_PARAM_MAP_KEYS), (_, k) => BOOKMARK_PARAM_MAP[k]);
    const copiedParams = pick(params, BOOKMARK_COPY_OVER_KEYS);

    const attrs = {
      ...classParams,
      ...convertedParams,
      ...copiedParams,
      name,
      id,
      userId,
      userName,
      userEmail,
      description,
      modifiedStr,

      // permissions
      can_view,
      can_update_basic,
      can_share,
      is_superadmin,
      allow_staff_override,
    };

    return new FlowsReport(attrs);
  }

  public static bookmarkToClassParams(params) {
    const attrs: Partial<FlowsReport> = {};

    if (params.steps) {
      attrs.events = params.steps.map(mapBookmarkStepToEventEntry.bind(null, []));
    }
    if (params.exclusions) {
      attrs.exclusionSteps = params.exclusions.map(
        mapBookmarkExclusionStepToEventEntry.bind(null, attrs.events.length, []),
      );
    }

    if (params.group_by) {
      attrs.groupBy = bookmarkGroupByToGroupBy(params.group_by);
    }

    // TODO: Split this into cohortFilter and userFilters when we switch over to the new API
    attrs.cohortFilters = bookmarkFilterByCohortToCohortFilters(params.filter_by_cohort);

    if (params.date_range) {
      attrs.dateRange = FullLengthDateRange.fromBookmarkFormat(params.date_range).getAttributes();
    }

    if (params.filter_by_event) {
      const filters = partition(params.filter_by_event.children, `event`);
      attrs.eventFilters = filters[0].map((filter) => filter.event);
      attrs.userFilters = filters[1].map((filter) => filter.user);
    }

    if (params.data_group_id) {
      attrs.dataGroupId = params.data_group_id;
    }

    return attrs;
  }

  public getEventsAsFlowQueryEntries(): FlowsQueryEventEntry[] {
    return this._events.map((event) => {
      return {type: SelectorTypeDefinitions.FlowsEvent, ...omit(event, [`type`])};
    });
  }

  get events() {
    return this._events;
  }

  set events(events: QueryEventEntry[]) {
    // The current implementation for alignment works for the toggle all alignment implementation.
    // If we later switch to controling the alignment per section, this implementation will have to be revisited.
    if (!this.alignment || this.alignment.length === 0) {
      // Reverse steps of the first anchor are always right aligned.
      this.alignment = [Alignment.RIGHT].concat(new Array<Alignment>(events.length).fill(Alignment.LEFT));
    } else if (events.length > 0 && events.length !== this.alignment.length - 1) {
      // Forward steps of the last anchor are always left aligned.
      const alignmentFill = this.getOverallAlignment();
      this.alignment = [Alignment.RIGHT]
        .concat(new Array<Alignment>(events.length - 1).fill(alignmentFill))
        .concat([Alignment.LEFT]);
    }
    this._events = this.generateFlowsEvents(events);
  }

  get eventSelection() {
    let eventSelection = [];
    if (this.events) {
      eventSelection = this.events.filter(Boolean);
    }
    return eventSelection;
  }

  get previousEventSelection() {
    return this._previousEventSelection;
  }

  get cohortGroupBys() {
    return this.groupBy.filter(isCohortSegment);
  }

  set funnelParams(funnelParams: FunnelParamsForFlows) {
    if (funnelParams && funnelParams.funnelBookmark && funnelParams.funnelBookmark[`count_type`]) {
      this.countType = fromEnum(funnelParams.funnelBookmark[`count_type`], CountType, CountType.Unique);
    }
    if (funnelParams && funnelParams.funnelBookmark && funnelParams.funnelBookmark[`aggregate_by`]) {
      this.aggregateBy = mapBookmarkToHoldPropConstant(funnelParams.funnelBookmark[`aggregate_by`]);
    }
    this._funnelParams = funnelParams;
  }

  get funnelParams() {
    return this._funnelParams;
  }

  get trackingParams() {
    return {
      'Anchor Events': this.eventSelection.map((entry: QueryEventEntry) => entry.event.label),
      'Custom event': this.eventSelection.some((entry: QueryEventEntry) => entry.event.custom),
      'Event type': this.eventSelection.map((entry: QueryEventEntry) =>
        entry.event.custom ? `Custom event` : `Event`,
      ),
      'Tab name': this.chartType,
      reverse: !!this.reverse,
      '# of filters': this.filters.length,
      '# of breakdowns': this.segments.length,
      '# of hidden events': this.hiddenEvents.length,
      '# of visible events': this.visibleEvents.length,
      '# of cohort filters': this.cohortFilters.length,
      '# of cohort segments': this.groupBy.map((segment) => getSegmentType(segment) === ResourceType.Cohort).length,
      'Query time Sampling': this.querySampling,
      Alignment: AlignmentOptions[this.getOverallAlignment()].label,
      '# of steps': this.eventSelection
        .map((event) => (event[`forward`] || 0) + (event[`reverse`] || 0))
        .reduce((acc, val) => acc + val, 0),
      '# of anchor events': this.eventSelection.length,
      'Time percetiles': this.timePercentiles.length,
      'Collapse Repeated': this.collapseRepeated,
      Cardinality: this.cardinality,
      ...(this.maxSteps === -1 ? {'Flows Version': `anchor steps`} : {}),
      'Drop off analysis mode': this.containsFunnelBookmark(),
    };
  }

  get filters(): PropertyFilter[] {
    return [
      ...this.eventFilters,
      ...this.userFilters,
      ...this.cohortFilters,
      ...(this.otherFilters ? this.otherFilters : []),
    ];
  }

  set filters(filters) {
    this.eventFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Event));
    this.userFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.User));
    this.cohortFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Cohort));
    this.otherFilters = constructPropertyFilters(filters.filter((item) => item.resourceType === ResourceType.Other));
  }

  public aggregateBy: Segmentation[];
  public alignment: Alignment[];
  public alignBreakdowns: boolean;
  public alwaysBreakdown: boolean;
  public anchorPosition: string;
  public cardinality: number;
  public chartType: ChartType;
  public cohortFilters: PropertyFilter[];
  public collapseRepeated: boolean;
  public countType: CountType;
  public dashboard: DashboardOrigin;
  public dataGroupId: string;
  public dateRange: Partial<FullLengthDateRange>;
  public description: string;
  public eventFilters: PropertyFilter[];
  public exclusionSteps: QueryEventEntry[];
  public _funnelParams: FunnelParamsForFlows;
  public graphSortPriority: string;
  public groupBy: Segment[];
  public hiddenEvents: string[];
  public id: number;
  public listSortPriority: string;
  public maxResults: number;
  public maxSteps: number;
  public name: string;
  public otherFilters: PropertyFilter[];
  public relativeBreakdowns: boolean;
  public reverse: boolean;
  public segments: Array<{
    event: EventItem;
    property: PropertyItem;
  }>;
  public showCustomEvents: boolean;
  // TODO: change timePercentiles to a boolean when Flows API is dropped
  public timePercentiles: number[];
  public uniqueFlows: boolean;
  public version: number;
  public visibleEvents: string[];

  private _events: QueryEventEntry[];
  private _previousEventSelection: QueryEventEntry[];

  /** transient, should not be persisted */
  public querySampling: boolean;
  public userFilters: PropertyFilter[];

  constructor(attrs = {}) {
    Object.assign(this, FLOWS_REPORT_DEFAULTS);
    Object.assign(this, pick(attrs, FLOWS_REPORT_DEFAULTS_KEYS));

    // Instantiate models from primitive attrs
    this.dateRange = new FullLengthDateRange(this.dateRange);
    this.events = this.events.map((entry) => ({...entry, filters: constructPropertyFilters(entry.filters)}));
    this.exclusionSteps = this.exclusionSteps.map((entry) => ({
      ...entry,
      filters: constructPropertyFilters(entry.filters),
    }));

    // force filter initialization
    // eslint-disable-next-line no-self-assign
    this.filters = this.filters;
  }

  public clone(attrs: Partial<FlowsReport> = {}) {
    return new FlowsReport({
      ...pick(this, [...FLOWS_REPORT_DEFAULTS_KEYS]),
      ...attrs,
    });
  }

  /**
   * Calculate which params are non-default, and serialize any
   * complex values (like dateRange)
   */
  public toUrlData() {
    const urlParams = URL_PARAMS.reduce((params, key) => {
      if (
        key !== `dateRange` ? !isEqual(this[key], DEFAULTS[key]) : !isEqual(this[key]?.getAttributes(), DEFAULTS[key])
      ) {
        params[key] = this[key];
      }
      return params;
    }, {});

    return isEmpty(urlParams)
      ? null
      : {
          ...urlParams,
          dateRange: this.dateRange.getAttributes(),
          version: this.version,
        };
  }

  public paramEquals(param, value) {
    switch (param) {
      case `dateRange`:
        return this.dateRange.equals(value);
      case `events`:
        return isEqual(this.events, this.generateFlowsEvents(value));
      default:
        return this[param] === value;
    }
  }

  public update(params: Partial<FlowsReport> = {}): boolean {
    let changed = false;

    // save the previous event selection before updating the report
    this._previousEventSelection = JSON.parse(JSON.stringify(this.eventSelection));

    for (const [k, v] of Object.entries(params)) {
      if (!this.paramEquals(k, v)) {
        changed = true;
        this[k] = v;
      }
    }

    if (`dateRange` in params) {
      this.dateRange = new FullLengthDateRange(params.dateRange);
    }

    // updating maxSteps requires re-generating the
    // events structure as it is derived from maxSteps
    if ((`maxSteps` in params && this.maxSteps !== params.maxSteps) || `reverse` in params) {
      // eslint-disable-next-line no-self-assign
      this.events = this.events;
    }

    return changed;
  }

  public generateBookmark(): FlowsBookmark {
    const steps = this.getEventsAsBookmark();
    const exclusions = this.getExclusionStepsAsBookmark();
    const includeNegatedCohort = this.cohortGroupBys.length > 0 && this.cohortGroupBys[0].length === 1;
    const groupBy = groupByToBookmarkGroupBy(this.groupBy, includeNegatedCohort);
    const aggregateBy = mapHoldPropConstantToBookmark(this.aggregateBy);

    let bookmark: FlowsBookmark = {
      ...(aggregateBy && aggregateBy.length ? {aggregate_by: aggregateBy} : {}),
      anchorPosition: this.anchorPosition,
      ...(this.anchorPosition === FlowAnchorPosition.Funnel
        ? {anchor_position: AnchorPosition.Funnel}
        : {anchor_position: AnchorPosition.StepAndFunnel}),
      cardinality_threshold: this.chartType === ChartType.Sankey ? this.cardinality : this.maxResults,
      collapse_repeated: this.collapseRepeated,
      ...(this.countType ? {count_type: this.countType} : {}),
      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
      date_range: this.dateRange.getBookmarkFormat(),
      exclusions,
      filter_by_cohort: JSON.stringify(this.cohortFilters.length ? getFilterByCohorts(this.cohortFilters) : {}),
      ...(this.hasFilterByEvent() ? {filter_by_event: this.getFilterByEvent()} : {}),
      flows_merge_type: CHART_TYPE_TO_QUERY_PARAM[this.chartType],
      ...(!isEmpty(groupBy) ? {group_by: groupBy} : {}),
      show_custom_events: this.showCustomEvents,
      segments: this.segments,
      steps,
      ...(this.timePercentiles.length ? {time_percentiles_enabled: true} : {}),
      version: this.version,
    };

    if (this.visibleEvents.length) {
      bookmark.visible_events = this.visibleEvents;
    } else {
      bookmark.hidden_events = this.hiddenEvents;
    }

    if (this.chartType === ChartType.Sankey) {
      bookmark.graph_sort_priority = this.graphSortPriority;
    } else {
      bookmark.list_sort_priority = this.listSortPriority;
    }

    if (this.cohortGroupBys.length) {
      if (this.relativeBreakdowns) {
        bookmark.graph_sort_priority = `relative`;
      }

      if (this.chartType === ChartType.Sankey) {
        bookmark.exclude_everything_else_user_segment = `true`;
      }
    }

    Object.assign(bookmark, pick(this, BOOKMARK_COPY_OVER_KEYS));

    if (this.funnelParams !== null) {
      const flowsBookmark = addFunnelBehaviorToFlowsBookmark(bookmark, this.funnelParams, false);
      bookmark = flowsBookmark;
    }

    return bookmark;
  }

  private getEventsAsBookmark(): BookmarkFunnelStep[] {
    return this.events.map(mapEventEntryToBookmarkStep.bind(null, this.getPropertySerializerConfig()));
  }

  private getExclusionStepsAsBookmark() {
    return this.exclusionSteps.map(
      mapEventEntryToBookmarkExclusionStep.bind(null, this.events.length, this.getPropertySerializerConfig()),
    );
  }

  /** return properties necessary for arb-selector conversion of property filters */
  private getPropertySerializerConfig(): SerializePropFilterContext {
    return {
      topLevelDataGroupId: this.dataGroupId,
      projectId: 0,
    };
  }

  /**
   * Truncates left to right to create a basic preview query for download mode
   */
  public truncateForDownload() {
    let allowedSteps = MAX_STEPS_FOR_DOWNLOAD - this.events.length;
    const events = this.events.map((event) => {
      if (allowedSteps > 0) {
        const {forward = 0, reverse = 0} = event;
        if (allowedSteps - forward - reverse >= 0) {
          allowedSteps -= forward + reverse;
          return event;
        } else {
          const truncatedReverse = Math.min(reverse, allowedSteps);
          allowedSteps -= truncatedReverse;
          const truncatedForward = Math.min(forward, allowedSteps);
          allowedSteps -= truncatedForward;

          return {...event, forward: truncatedForward, reverse: truncatedReverse};
        }
      }
      return {...event, forward: 0, reverse: 0};
    });
    const cardinality = Math.min(this.cardinality, CARDINALITY_FOR_DOWNLOAD);
    const maxResults = Math.min(this.maxResults, MAX_RESULTS_FOR_DOWNLOAD);

    return this.clone({maxSteps: -1, events, cardinality, maxResults});
  }

  /**
   * Generates events with forward and reverse fields
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private generateFlowsEvents(events: QueryEventEntry[]): QueryEventEntry[] {
    // Setting maxSteps to -1 disables the legacy way of controlling steps and lets the events themselves control
    // the forward and reverse steps independently.
    if (this.maxSteps === -1) return events;

    const maxSteps = this.chartType === ChartType.Paths ? 5 : this.maxSteps || 3;
    // handle non-beta single event anchorSelection
    if (events.length === 1 && events[0]) {
      const entry = omit(events[0], `forward`, `reverse`) as QueryEventEntry;
      return [
        {
          ...entry,
          event: entry.event,
          [this.reverse ? `reverse` : `forward`]: maxSteps,
        },
      ];
    }

    // fill left to right direction for all anchors except last
    // allows for setting reverse flows without explicitly
    // defining the `reverse` property
    const isSparse = events.some((entry) => isEmpty(entry));

    return events.map((entry, index) => {
      if (isEmpty(entry)) {
        return null;
      }

      entry = {
        ...(omit(entry, `forward`, `reverse`) as QueryEventEntry),
        event: entry.event,
      };

      if (this.reverse) {
        entry[`reverse`] = maxSteps;
      } else if (index === events.length - 1) {
        entry[`reverse`] = isSparse ? maxSteps : 0;
      } else {
        entry[`forward`] = maxSteps;
      }

      return entry;
    });
  }

  public hasFilterByEvent() {
    // TODO: Remove userFilters length check once we switch to the new API
    return this.eventFilters.length > 0 || this.userFilters.length > 0 || this.otherFilters.length > 0;
  }

  public getFilterByEvent() {
    return {
      operator: FiltersOperator.And,
      children: [
        ...this.eventFilters.map((filter) => ({event: filter})),
        ...this.userFilters.map((filter) => ({user: filter})),
        ...this.otherFilters.map((filter) => ({other: filter})),
      ],
    };
  }

  public getOverallAlignment() {
    return this.alignment && this.alignment.length >= 2 ? this.alignment[1] : Alignment.LEFT;
  }

  public containsFunnelBookmark(): boolean {
    return this.funnelParams !== null && this.funnelParams.funnelBookmark !== null;
  }

  public getFunnelBookmark(): FunnelBookmark {
    return this.containsFunnelBookmark ? this.funnelParams.funnelBookmark : null;
  }
}
